import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag, ArrowRight, Tag, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getCart, removeFromCart, clearCart } from '../lib/cart';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { useToast } from '../hooks/use-toast';

export default function Cart() {
  const [items, setItems] = useState(getCart());
  const [coupon, setCoupon] = useState('');
  const [applied, setApplied] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const subtotal = items.reduce((s, i) => s + i.discountPrice, 0);
  const original = items.reduce((s, i) => s + i.price, 0);
  const savings = original - subtotal;
  const discount = applied?.discount || 0;
  const total = Math.max(0, subtotal - discount);

  const remove = (id) => { setItems(removeFromCart(id)); toast({ title: 'Removed from cart' }); };

  const applyCoupon = async () => {
    if (!coupon) return;
    try {
      const res = await api.applyCoupon(coupon.trim().toUpperCase(), subtotal);
      setApplied(res);
      toast({ title: 'Coupon applied!', description: `${res.percent}% off` });
    } catch (e) {
      toast({ title: 'Invalid coupon', description: e.response?.data?.detail || 'Try another', variant: 'destructive' });
    }
  };

  const checkout = async () => {
    if (!user) { navigate('/login?redirect=/cart'); return; }
    try {
      await api.checkout(items.map(i => i.id), applied?.code);
      clearCart();
      setItems([]);
      toast({ title: 'Order successful!', description: 'You are enrolled in your courses.' });
      navigate('/dashboard');
    } catch (e) {
      toast({ title: 'Checkout failed', variant: 'destructive' });
    }
  };

  if (items.length === 0) {
    return (
      <div className="pt-16 min-h-screen bg-purple-50/40">
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="w-24 h-24 mx-auto rounded-full bg-white grid place-items-center mb-5 shadow-lg">
            <ShoppingBag size={44} className="text-[#6b47dc]" />
          </div>
          <h1 className="text-3xl font-extrabold mb-3">Your cart is empty</h1>
          <p className="text-gray-600 mb-6">Looks like you haven't added any courses yet.</p>
          <Link to="/courses"><Button className="bg-[#6b47dc] hover:bg-[#5a3ac0] text-white font-bold h-12 px-8 btn-shine">Browse courses <ArrowRight size={16} className="ml-1" /></Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-purple-50/30">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-10">
        <h1 className="text-3xl md:text-4xl font-extrabold mb-8">Shopping cart</h1>
        <div className="grid lg:grid-cols-[1fr_380px] gap-8">
          <div className="space-y-4">
            {items.map(item => (
              <div key={item.id} className="bg-white rounded-2xl border border-gray-100 p-4 flex gap-4 hover:shadow-md transition-shadow">
                <img src={item.thumbnail} alt={item.title} className="w-32 h-24 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <Link to={`/course/${item.id}`} className="font-bold text-gray-900 hover:text-[#6b47dc] block truncate">{item.title}</Link>
                  <p className="text-xs text-gray-500 mt-0.5">{item.tagline}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-600">
                    <span>{item.duration}</span><span>•</span><span>{item.level}</span><span>•</span><span>By {item.instructor}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end justify-between">
                  <button onClick={() => remove(item.id)} className="text-gray-400 hover:text-red-500"><Trash2 size={18} /></button>
                  <div className="text-right">
                    <div className="font-extrabold text-lg">${Number(item.discountPrice).toLocaleString()}</div>
                    <div className="text-xs text-gray-400 line-through">${Number(item.price).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-extrabold text-lg mb-4">Order summary</h3>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between"><span className="text-gray-600">Original price</span><span className="line-through text-gray-400">${original.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-semibold">${subtotal.toLocaleString()}</span></div>
                <div className="flex justify-between text-emerald-600"><span>You save</span><span className="font-bold">- ${savings.toLocaleString()}</span></div>
                {applied && <div className="flex justify-between text-emerald-600"><span>Coupon ({applied.code})</span><span className="font-bold">- ${discount.toLocaleString()}</span></div>}
              </div>
              <div className="border-t border-gray-100 pt-3 flex justify-between font-extrabold text-xl mb-5"><span>Total</span><span>${total.toLocaleString()}</span></div>

              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon (try EDU10)" className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 focus:border-[#6b47dc] outline-none text-sm" />
                </div>
                <Button variant="outline" onClick={applyCoupon} disabled={!!applied}>{applied ? 'Applied' : 'Apply'}</Button>
              </div>

              <Button onClick={checkout} className="w-full bg-[#ff5722] hover:bg-[#e64a19] text-white font-bold h-12 btn-shine">Checkout • ${total.toLocaleString()}</Button>
              <div className="mt-4 flex items-center gap-2 text-xs text-gray-500"><ShieldCheck size={14} className="text-emerald-500" /> Secure checkout. 7-day money-back guarantee.</div>
            </div>

            <div className="bg-gradient-to-br from-[#6b47dc] to-[#ff5722] rounded-2xl p-5 text-white">
              <Sparkles size={18} className="mb-2" />
              <div className="font-bold">Get 30% off your next enrollment</div>
              <div className="text-xs opacity-90 mt-1">Auto-applied on your next purchase after checkout.</div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
